/** Shared SuperAdmin plan form helpers — employee vs patient charge sections. */

export const CHARGE_MODES = [
  { id: "EMPLOYEES", label: "Employees only" },
  { id: "PATIENTS", label: "Patients only" },
  { id: "BOTH", label: "Employees + Patients" },
];

export const BILLING_CYCLES = [
  { id: "MONTHLY", months: 1, label: "Monthly" },
  { id: "QUARTERLY", months: 3, label: "Quarterly" },
  { id: "SEMI_ANNUAL", months: 6, label: "Semi-annual" },
  { id: "ANNUAL", months: 12, label: "Annual" },
];

export function chargeModeFromFlags(chargeEmployees, chargePatients) {
  if (chargeEmployees && chargePatients) return "BOTH";
  if (chargePatients) return "PATIENTS";
  return "EMPLOYEES";
}

export function flagsFromChargeMode(mode) {
  return {
    chargeEmployees: mode === "EMPLOYEES" || mode === "BOTH",
    chargePatients: mode === "PATIENTS" || mode === "BOTH",
  };
}

export function emptyPlanForm() {
  return {
    name: "",
    description: "",
    baseAmount: "99",
    patientBaseAmount: "99",
    currency: "USD",
    billingCycle: "MONTHLY",
    billingCycleMonths: "1",
    billingChargeMode: "EMPLOYEES",
    includedUsers: "10",
    pricePerExtraUser: "5",
    maxUsers: "",
    includedPatients: "5",
    pricePerExtraPatient: "5",
    maxPatients: "",
    paymentModel: "POSTPAID",
    gracePeriodDays: "7",
    autoSuspend: true,
    status: "Active",
    syncActiveAssignments: false,
  };
}

export function planToForm(plan) {
  const mode =
    plan.billingChargeMode ||
    chargeModeFromFlags(plan.chargeEmployees, plan.chargePatients);
  const flags = flagsFromChargeMode(mode);
  const storedPatient = Number(plan.patientBaseAmount);
  const patientBasic =
    storedPatient > 0
      ? storedPatient
      : flags.chargePatients && !flags.chargeEmployees
        ? plan.baseAmount ?? 0
        : plan.patientBaseAmount ?? 0;
  return {
    ...emptyPlanForm(),
    name: plan.name || "",
    description: plan.description || "",
    baseAmount: String(plan.baseAmount ?? ""),
    patientBaseAmount: String(patientBasic),
    currency: plan.currency || "USD",
    billingCycle: plan.billingCycle || "MONTHLY",
    billingCycleMonths: String(plan.billingCycleMonths ?? 1),
    billingChargeMode: mode,
    includedUsers: String(plan.includedUsers ?? 0),
    pricePerExtraUser: String(plan.pricePerExtraUser ?? 0),
    maxUsers: plan.maxUsers == null ? "" : String(plan.maxUsers),
    includedPatients: String(plan.includedPatients ?? 0),
    pricePerExtraPatient: String(plan.pricePerExtraPatient ?? 0),
    maxPatients: plan.maxPatients == null ? "" : String(plan.maxPatients),
    paymentModel: plan.paymentModel || "POSTPAID",
    gracePeriodDays: String(plan.gracePeriodDays ?? 7),
    autoSuspend: plan.autoSuspend !== false,
    status: plan.status || "Active",
  };
}

export function snapshotToForm(snapshot = {}, fallback = {}) {
  return planToForm({ ...fallback, ...snapshot });
}

function toNumber(value, fallback = 0) {
  if (value === "" || value == null) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toNullableInt(value) {
  if (value === "" || value == null) return null;
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function formToPlanPayload(form) {
  const flags = flagsFromChargeMode(form.billingChargeMode);
  const cycle = BILLING_CYCLES.find((c) => c.id === form.billingCycle);
  return {
    name: form.name.trim(),
    description: form.description?.trim() || null,
    baseAmount: flags.chargeEmployees
      ? toNumber(form.baseAmount)
      : toNumber(form.patientBaseAmount),
    patientBaseAmount: flags.chargePatients
      ? toNumber(form.patientBaseAmount)
      : 0,
    currency: form.currency || "USD",
    billingCycle: form.billingCycle,
    billingCycleMonths: cycle?.months ?? toNumber(form.billingCycleMonths, 1),
    billingChargeMode: form.billingChargeMode,
    chargeEmployees: flags.chargeEmployees,
    chargePatients: flags.chargePatients,
    includedUsers: flags.chargeEmployees ? toNumber(form.includedUsers, 0) : 0,
    pricePerExtraUser: flags.chargeEmployees
      ? toNumber(form.pricePerExtraUser, 0)
      : 0,
    maxUsers: flags.chargeEmployees ? toNullableInt(form.maxUsers) : null,
    includedPatients: flags.chargePatients
      ? toNumber(form.includedPatients, 0)
      : 0,
    pricePerExtraPatient: flags.chargePatients
      ? toNumber(form.pricePerExtraPatient, 0)
      : 0,
    maxPatients: flags.chargePatients ? toNullableInt(form.maxPatients) : null,
    paymentModel: form.paymentModel,
    gracePeriodDays: toNumber(form.gracePeriodDays, 7),
    autoSuspend: Boolean(form.autoSuspend),
    status: form.status,
  };
}

export const FIELD =
  "block w-full h-9 px-3 text-sm border border-slate-200 rounded-md outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";
export const LABEL = "block text-xs font-medium text-slate-600 mb-1";
